import {
  AttachmentImage,
  AttachmentVideo,
  AttachmentLink,
} from "@/components/ui/attachment-media";
import { useState } from "react";
import { Button, Icons, ImageLightbox } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import {
  attachmentFormat,
  attachmentIsImage,
  attachmentIsVideo,
} from "@/lib/composer-attachment";
import type { ImageAttachment } from "./image-preview-strip";

const ATTACHMENT_CHIP =
  "flex min-w-0 items-center gap-2 rounded-preview border border-glass-stroke-subtle p-1 pr-2";

export function MessageAttachmentStrip({
  attachments = [],
}: {
  attachments?: ImageAttachment[];
}) {
  const t = useT(inputBarMessages);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  if (!attachments.length) return null;
  return (
    <div
      className="flex max-w-full flex-wrap gap-2"
      data-testid="message-attachment-strip"
    >
      {attachments.map((attachment) => {
        const url = attachment.url;
        if (attachmentIsVideo(attachment))
          return (
            <div
              key={attachment.id}
              className={ATTACHMENT_CHIP}
              data-testid={`message-attachment-${attachment.id}`}
            >
              <AttachmentVideo
                src={url}
                controls
                preload="metadata"
                className="max-h-48 max-w-80 rounded-preview"
                aria-label={`Play ${attachment.filename || "video"}`}
                data-testid={`message-video-${attachment.id}`}
              />
              <AttachmentLabel attachment={attachment} />
            </div>
          );
        if (attachmentIsImage(attachment)) {
          return (
            <Button
              key={attachment.id}
              type="button"
              variant="ghost"
              onClick={() => setPreviewUrl(url)}
              className={cn(
                ATTACHMENT_CHIP,
                "h-auto justify-start text-left font-normal",
              )}
              aria-label={`Preview ${attachment.filename || "image"}`}
              data-testid={`message-attachment-${attachment.id}`}
            >
              <AttachmentImage
                src={url}
                alt={attachment.filename || t("attachedImage")}
                className="h-10 w-10 rounded-preview object-cover"
              />
              <AttachmentLabel attachment={attachment} />
            </Button>
          );
        }
        return (
          <AttachmentLink
            key={attachment.id}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={ATTACHMENT_CHIP}
            data-testid={`message-attachment-${attachment.id}`}
          >
            <Icons.FileText className="h-10 w-10 p-2 text-muted" />
            <AttachmentLabel attachment={attachment} />
          </AttachmentLink>
        );
      })}
      {previewUrl ? (
        <ImageLightbox src={previewUrl} onClose={() => setPreviewUrl(null)} />
      ) : null}
    </div>
  );
}

function AttachmentLabel({ attachment }: { attachment: ImageAttachment }) {
  const t = useT(inputBarMessages);
  return (
    <span className="flex min-w-0 max-w-40 flex-col">
      <span className="truncate text-appkit-caption text-primary">
        {attachment.filename || t("image")}
      </span>
      <span className="text-appkit-mini text-muted">
        {attachmentFormat(attachment)}
      </span>
    </span>
  );
}
